import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentData } from "firebase/firestore";
import { insightsCollection } from "./insightsCollection";
import { db, auth, signOut, getImageFromStorage } from "./firebase";
import { logAppEvent } from "./Helpers/EventLogging";
import {
  FaPlus,
  FaBars,
  FaExternalLinkAlt,
  FaEllipsisV,
  FaFileImport,
  FaFileExport,
} from "react-icons/fa"; // Import the plus, bars, and external link icons
import IntegrationCard from "./Components/IntegrationCard";
import FlowCard from "./Components/FlowCard";
import { Flow, LawyerData } from "./types/types";
// import { Tab, Tabs, TabList, TabView, TabPanel } from "react-tabs"; // Import react-tabs
import BurgerMenu from "./Components/BurgerMenu"; // Import the new BurgerMenu component
import { TabView, TabPanel } from "primereact/tabview";
import { DataView, DataViewLayoutOptions } from "primereact/dataview";
import { formatTimestamp } from "./Helpers/Calculations";
import placeholder from "./assets/focusmap-placeholder.png";
import { deleteFlowData } from "./storage/storageProxy";
import {
  loadUserTemplates,
  saveUserTemplate,
  deleteUserTemplate,
  exportTemplateToFile,
  importTemplateFromFile,
  UserTemplate,
} from "./storage/templateStorage";
import TemplateEditorDialog from "./Components/TemplateEditorDialog";
import TemplatePreview from "./Components/TemplatePreview";

import {
  fetchFlows,
  getUserPlan,
  getBusinessModuleSubscriber,
  getUserDoc,
  checkUserPlanAndFlowLimit,
  getUserDataByEmail,
  updateUserShowWelcome,
  updateUserUseLocalData,
} from "./storage/storageProxy"; // Import fetchFlows from firestoreService
import { IoHelpCircle } from "react-icons/io5"; // Import the help icon
import { FaCloud, FaDatabase } from "react-icons/fa";
import HelpModal from "./Components/HelpModal"; // Import the HelpModal component
import CustomAlert from "./Components/CustomAlert"; // Import CustomAlert component
import WelcomeModal from "./Components/WelcomeModal"; // Import the WelcomeModal component
// import { templates } from "./templates";
import { integrations } from "./Data/integrations";
import OneNoteIntegrationModal from "./Components/OneNoteIntegrationModal"; // Import the new OneNoteIntegrationModal component
import LaywerTemplate from "./Components/SystemTemplates/LawyerTemplate"; // Import the LawyerTemplate component
import devBanner from "./assets/images/preview.png"; // Import the dev-banner image
import { gt, set } from "lodash";
import { useStore } from "./App/store";
import { Dialog } from "primereact/dialog";
import { SelectButton } from "primereact/selectbutton";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { PrimeIcons } from "primereact/api";
import { doc, updateDoc } from "firebase/firestore";

interface FlowCardProps {
  flow: Flow;
  handleFlowClick: (id: string) => void;
}

const Homepage: React.FC = () => {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [displayedFlows, setDisplayedFlows] = useState<Flow[]>([]);
  const [batchSize] = useState(12); // Number of cards to load per batch
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showLawyerTemplate, setShowLawyerTemplate] = useState(false);
  const [selectedHelpTopic, setSelectedHelpTopic] = useState<string | null>(
    null,
  );
  const [showMapLimitAlert, setShowMapLimitAlert] = useState(false);
  const [showFeatureAlert, setShowFeatureAlert] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [showOneNoteModal, setShowOneNoteModal] = useState(false);
  const [layout, setLayout] = useState<"grid" | "list">("list");
  const [alertTitle, setAlertTitle] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showRenameConfirmModal, setShowRenameConfirmModal] = useState(false);
  const [flowName, setFlowName] = useState("");
  const [currentFlowId, setCurrentFlowId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [userTemplates, setUserTemplates] = useState<UserTemplate[]>([]);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<UserTemplate | null>(
    null,
  );
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewMarkdown, setPreviewMarkdown] = useState("");
  const [templateMenuVisible, setTemplateMenuVisible] = useState<string | null>(
    null,
  );

  const systemTemplates = useMemo(() => {
    // Load system templates authored as Markdown in src/Templates/*.md.
    // These are the templates we want to show on the Homepage “Templates” tab.
    const modules = import.meta.glob("./Templates/*.md", {
      query: "?raw",
      import: "default",
      eager: true,
    }) as Record<string, string>;

    const extractFirstH1 = (markdown: string): string | null => {
      const match = markdown.match(/^#\s+(.+?)\s*$/m);
      return match ? match[1].trim() : null;
    };

    const extractAuthor = (markdown: string): string | null => {
      const match = markdown.match(/^Author:\s*(.+?)\s*$/m);
      return match ? match[1].trim() : null;
    };

    const extractImage = (markdown: string): string | null => {
      const match = markdown.match(/^Image:\s*(.+?)\s*$/m);
      return match ? match[1].trim() : null;
    };

    const extractDescription = (markdown: string): string | null => {
      const match = markdown.match(/^Description:\s*(.+?)\s*$/m);
      return match ? match[1].trim() : null;
    };

    const list = Object.entries(modules)
      .map(([filePath, markdown]) => {
        const file = filePath.split("/").pop() || filePath;
        const id = file.replace(/\.md$/i, "");
        const lowerId = id.toLowerCase();
        const isDocOnly =
          lowerId === "readme" ||
          lowerId === "master" ||
          lowerId === "template_authoring" ||
          lowerId === "template-authoring" ||
          lowerId.startsWith("_");
        if (isDocOnly) return null;

        const name = extractFirstH1(markdown) || id;
        const author = extractAuthor(markdown);
        const description = extractDescription(markdown);
        const image = extractImage(markdown);
        return { id, name, author, description, image };
      })
      .filter(Boolean) as Array<{
      id: string;
      name: string;
      author: string | null;
      description: string | null;
      image: string | null;
    }>;

    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, []);

  const navigate = useNavigate();
  const user = auth.currentUser;

  const menuRef = useRef<HTMLDivElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLDivElement>,
    flowId: string | null,
    action: string,
  ) => {
    event.stopPropagation();
    setCurrentFlowId(flowId);
    if (action === "rename") {
      setShowRenameConfirmModal(true);
    } else if (action === "delete") {
      setShowDeleteConfirmModal(true);
    } else {
      alert("Not yet available");
    }
    setShowMenu(false);
  };

  const handleConfirmDelete = async (event: React.MouseEvent) => {
    try {
      await deleteFlowData(currentFlowId, useStore.getState().useLocalStorage);
    } catch (error) {
      console.error("Error deleting document: ", error);
      alert("Failed to delete document");
    }
    event.stopPropagation();
    setShowDeleteConfirmModal(false);
    loadFlows();
  };

  const handleCancelDelete = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowDeleteConfirmModal(false);
  };

  const handleConfirmRename = async (
    event: React.MouseEvent,
    renamed: string,
  ) => {
    event.stopPropagation();
    try {
      await updateDoc(doc(db, "flows", currentFlowId), { name: renamed });
      setFlowName(renamed);
    } catch (error) {
      console.error("Error renaming document: ", error);
      alert("Failed to rename document");
    }
    setShowRenameConfirmModal(false);
  };

  const handleCancelRename = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowRenameConfirmModal(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };

  useEffect(() => {
    loadFlows();
    loadUserTemplatesData();
    logAppEvent("activity", { type: "load", name: "HomePage" });
  }, [user, useStore.getState().useLocalStorage]);

  const loadUserTemplatesData = async () => {
    if (user && user.uid) {
      const templates = await loadUserTemplates(
        user.uid,
        useStore.getState().useLocalStorage,
      );
      setUserTemplates(templates);
    }
  };

  const loadFlows = async () => {
    if (user) {
      if (user.email) {
        setFlows([]);
        setDisplayedFlows([]);
        const flowsData = await fetchFlows(
          user.email,
          useStore.getState().useLocalStorage,
        );
        const sortedData = sortData(flowsData);
        setFlows(sortedData as Flow[]);
        // Load first batch immediately
        setDisplayedFlows((sortedData as Flow[]).slice(0, batchSize));
        setHasMore((sortedData as Flow[]).length > batchSize);
      }

      fetchAvailableSources();
    }
  };

  // Load more flows when scrolling
  const loadMoreFlows = () => {
    const currentLength = displayedFlows.length;
    const nextBatch = flows.slice(currentLength, currentLength + batchSize);
    if (nextBatch.length > 0) {
      setDisplayedFlows([...displayedFlows, ...nextBatch]);
      setHasMore(currentLength + nextBatch.length < flows.length);
    } else {
      setHasMore(false);
    }
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreFlows();
        }
      },
      { threshold: 0.1 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [displayedFlows, hasMore, flows]);

  // Update displayed flows when search changes
  useEffect(() => {
    if (searchQuery) {
      const filtered = flows.filter((flow) =>
        flow.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setDisplayedFlows(filtered.slice(0, batchSize));
      setHasMore(filtered.length > batchSize);
    } else {
      setDisplayedFlows(
        flows.slice(0, Math.max(displayedFlows.length, batchSize)),
      );
      setHasMore(flows.length > displayedFlows.length);
    }
  }, [searchQuery]);

  const fetchAvailableSources = async () => {
    if (user) {
      const userDocument = await getUserDoc(user.uid, false);
      const availableSources = insightsCollection.map((item) => item.data.key);
      const userSelectedSources = Object.keys(
        userDocument?.insights || {},
      ).filter((key) => userDocument?.insights[key].checked);
      // console.log("User selected sources", userSelectedSources);

      // Always include Brainstorm
      const userInsights = availableSources.filter(
        (source: string) =>
          source === "brainstorm" || // Always include brainstorm
          (Array.isArray(userSelectedSources) &&
            userSelectedSources.includes(source)),
      );
      const updatedUserInsights = userInsights.map((source: string) => {
        const matchingItem = insightsCollection.find(
          (item) => item.data.key === source,
        );
        return matchingItem ? matchingItem.data.title : source;
      });
      // console.log("Updated User Insights", updatedUserInsights);
      useStore.getState().setSourceOptions(updatedUserInsights);
    }
  };

  const reSortData = (id: string, isFavourite: boolean) => {
    const updatedFlows = flows.map((flow) => {
      if (flow.id === id) {
        return { ...flow, favourite: isFavourite };
      }
      return flow;
    });
    const sortedData = sortData(updatedFlows);
    setFlows(sortedData);
  };

  const sortData = (data: any) => {
    data.sort((a: Flow, b: Flow) => {
      if (a.favourite && !b.favourite) return -1;
      if (!a.favourite && b.favourite) return 1;

      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);

      // Validate if date is Invalid
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        throw new Error("Invalid date format detected");
      }
      return dateB.getTime() - dateA.getTime(); // Descending order
    });
    return data;
  };

  const showAlert = (title: string, message: string) => {
    setAlertTitle(title);
    setAlertMessage(message);
  };

  const handleCloseAlert = () => {
    setAlertTitle(null);
    setAlertMessage(null);
  };

  useEffect(() => {
    const checkShowWelcome = async () => {
      if (user) {
        const userDoc = await getUserDoc(
          user.uid,
          useStore.getState().useLocalStorage,
        );
        if (userDoc && userDoc.showWelcome) {
          setShowWelcomeModal(true);
        }
      }
    };

    checkShowWelcome();
  }, [user]);

  useEffect(() => {
    //setShowHelpModal(true);
  }, [showHelpModal]);

  const handleFlowClick = (flowId: string) => {
    navigate(`/flow/${flowId}`);
  };

  const handleIntegrationClick = async (templateId: string) => {
    if (user) {
      const userPlan = await getUserPlan(
        user,
        useStore.getState().useLocalStorage,
      );
      if (userPlan && userPlan.includes("free")) {
        setShowFeatureAlert(true);
        return;
      }
    }

    setShowOneNoteModal(true);
  };

  const handleLawyerIntegrationClick = async (templateId: string) => {
    if (user) {
      const subscriber = await getBusinessModuleSubscriber(
        user.email!,
        templateId,
        useStore.getState().useLocalStorage,
      );
      if (!subscriber) {
        setShowFeatureAlert(true);
        return;
      }
    }
    setShowLawyerTemplate(true);
  };

  const handleSustainabilityIntegrationClick = async (templateId: string) => {
    if (user) {
      const subscriber = await getBusinessModuleSubscriber(
        user.email!,
        templateId,
        useStore.getState().useLocalStorage,
      );
      if (!subscriber) {
        setShowFeatureAlert(true);
        return;
      }
    }
    handleCreateNewFlow(templateId);
  };

  const handleMicrosoft365IntegrationClick = async (templateId: string) => {
    if (user) {
      const subscriber = await getBusinessModuleSubscriber(
        user.email!,
        templateId,
        useStore.getState().useLocalStorage,
      );
      if (!subscriber) {
        setShowFeatureAlert(true);
        return;
      }
    }
    handleCreateNewFlow(templateId);
  };

  const handleRapidLearningCyclesClick = async () => {
    // Rapid Learning Cycles is shipped as a system template JSON in /public/templates.
    // Flow will load it through the standard template import path.
    navigate(`/flow/new?template=rapidlearningcycles`);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/signin");
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const handleCreateNewFlow = async (
    template: string | null,
    data?: LawyerData,
  ) => {
    // If a specific template/integration was requested, go straight to it.
    if (template) {
      navigate(
        `/flow/new?template=${template}`,
        data ? { state: { data } } : {},
      );
      return;
    }

    // Always start blank when clicking “Create New Map”.
    navigate("/flow/new");
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleUserProfileClick = async () => {
    if (user) {
      let userData: DocumentData | null = null;
      if (user.email) {
        userData = await getUserDataByEmail(
          user.email,
          useStore.getState().useLocalStorage,
        );
      }
      if (userData) {
        // console.log("userData", userData);
        setUserProfile(userData);
        setShowProfileModal(true);
      } else {
        alert("No such document!");
        setShowProfileModal(false);
      }
    }
    setShowMenu(!showMenu);
  };

  const handleSettingsClick = async () => {
    if (user) {
      let userData: DocumentData | null = null;
      if (user.email) {
        userData = await getUserDataByEmail(
          user.email,
          useStore.getState().useLocalStorage,
        );
      }
      if (userData) {
        // console.log("userData", userData);
        setUserProfile(userData);
        setUserSettingsVisible(true);
      } else {
        alert("No such document!");
        setUserSettingsVisible(false);
      }
    }
    setShowMenu(!showMenu);
  };

  const handleDbSettingsClick = async () => {
    if (user) {
      let userData: DocumentData | null = null;
      if (user.email) {
        userData = await getUserDataByEmail(
          user.email,
          false, //useStore.getState().useLocalStorage
        );
      }
      if (userData) {
        // console.log("userData", userData);
        setDataOnline(userData.useLocalData ? options[0] : options[1]);
        setUserSettingsVisible(true);
      } else {
        alert("No such document!");
        setUserSettingsVisible(false);
      }
    }
  };

  const handlePrivacyPolicyClick = async () => {
    if (user) {
      window.open("https://www.focusmap.online/privacy-policy", "_blank");
    }
    setShowMenu(!showMenu);
  };

  const handleTermsAndConditionsClick = async () => {
    window.open("https://www.focusmap.online/terms-and-coniditions", "_blank");
    setShowMenu(!showMenu);
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
  };

  const handleHelp = () => {
    setShowHelpModal(true);
  };

  const handleCloseHelpModal = () => {
    setShowHelpModal(false);
  };

  const handleHelpTopicClick = (topic: string) => {
    setSelectedHelpTopic(topic);
  };

  const handleSubscriptionPlansClick = async () => {
    window.open("https://www.focusmap.online/subscriptions", "_blank");
    setShowMenu(!showMenu);
  };

  const handleCloseVideoModal = () => {
    setSelectedHelpTopic(null);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "unknown date";
    const date = new Date(timestamp.seconds * 1000);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  // const handleFocus365Click = () => {
  //   navigate("/Focus365");
  //   setShowMenu(!showMenu);
  // };

  const handleCloseWelcomeModal = async (showHelp: boolean) => {
    if (doNotShowAgain && user) {
      await updateUserShowWelcome(
        user.uid,
        false,
        useStore.getState().useLocalStorage,
      );
    }
    setShowWelcomeModal(false);
    if (showHelp) {
      setShowHelpModal(true);
    }
  };

  // const showUserSettings = async () => {
  //   const authUser = auth.currentUser;
  //   if (authUser) {
  //     const user: any = await getUserDoc(
  //       authUser.uid,
  //       dataOnline == options[0] ? true : false
  //     );
  //     setDataOnline(user?.useLocalData ? options[0] : options[1]);
  //     setUserSettingsVisible(true);
  //   }
  // };

  function saveUserSettings() {
    if (dataOnline === null) {
      showAlert("Alert", "Please select a data storage option");
      return;
    }
    const authUser = auth.currentUser;
    if (authUser) {
      //if (useStore.getState().useLocalStorage === true) {
      updateUserUseLocalData(authUser.uid, dataOnline == options[0]); //dataOnline == options[0]);
      useStore.getState().setUseLocalStorage(dataOnline == options[0]);
      //}
    }

    setUserSettingsVisible(false);
  }

  const headerElement = (
    <div className="inline-flex align-items-center justify-content-center gap-2">
      <span className="font-bold white-space-nowrap">User Settings</span>
    </div>
  );

  const footerContent = (
    <div>
      <Button
        label="Ok"
        icon="pi pi-check"
        onClick={() => saveUserSettings()}
        autoFocus
      />
    </div>
  );

  const [userSettingsVisible, setUserSettingsVisible] = useState(false);
  const options = ["Local Data", "Cloud Data"];
  const [dataOnline, setDataOnline] = useState(
    useStore.getState().useLocalStorage ? options[0] : options[1],
  );
  //const [userLocalData, setUseLocalData] = useState(false);

  // Add a state to hold image URLs for each flow
  const [flowImages, setFlowImages] = useState<Record<string, string>>({});

  // Fetch images for flows after flows are loaded
  useEffect(() => {
    const fetchImages = async () => {
      const images: Record<string, string> = {};

      // First, try to load all images from IndexedDB (instant, no network)
      const { loadAllFlowImages } = await import("./storage/dexie-db");
      const flowIds = flows.map((flow) => flow.id);
      const localImages = await loadAllFlowImages(flowIds);

      // Use local images where available
      Object.assign(images, localImages);

      // For flows without local images, fetch from Firebase Storage
      const missingImageFlows = flows.filter((flow) => !localImages[flow.id]);

      if (missingImageFlows.length > 0) {
        await Promise.all(
          missingImageFlows.map(async (flow) => {
            try {
              const img = await getImageFromStorage(`${flow.id}.png`);
              if (img) {
                images[flow.id] = img;
                // Save to IndexedDB for next time
                const { saveFlowImage } = await import("./storage/dexie-db");
                await saveFlowImage(flow.id, img);
              }
            } catch {
              // Silently handle missing images
            }
          }),
        );
      }

      setFlowImages(images);
    };
    if (flows.length > 0) fetchImages();
  }, [flows]);

  // Handle template actions
  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setShowTemplateEditor(true);
  };

  const handleEditTemplate = (template: UserTemplate) => {
    setEditingTemplate(template);
    setShowTemplateEditor(true);
    setTemplateMenuVisible(null);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!user) return;
    if (!confirm("Are you sure you want to delete this template?")) return;

    await deleteUserTemplate(
      user.uid,
      templateId,
      useStore.getState().useLocalStorage,
    );
    await loadUserTemplatesData();
    setTemplateMenuVisible(null);
  };

  const handleSaveTemplate = async (markdown: string) => {
    if (!user) return;

    const templateId = editingTemplate ? editingTemplate.templateId : null;

    await saveUserTemplate(
      user.uid,
      templateId,
      markdown,
      useStore.getState().useLocalStorage,
    );
    await loadUserTemplatesData();
    setShowTemplateEditor(false);
    setEditingTemplate(null);
  };

  const handlePreviewTemplate = (markdown: string) => {
    setPreviewMarkdown(markdown);
    setShowTemplatePreview(true);
    setTemplateMenuVisible(null);
  };

  const handleImportTemplate = async () => {
    if (!user) return;

    try {
      const markdown = await importTemplateFromFile();
      if (markdown) {
        await saveUserTemplate(
          user.uid,
          null, // New template, no existing ID
          markdown,
          useStore.getState().useLocalStorage,
        );
        await loadUserTemplatesData();
      }
    } catch (error) {
      console.error("Error importing template:", error);
      alert("Failed to import template");
    }
  };

  const handleExportTemplate = async (template: UserTemplate) => {
    try {
      await exportTemplateToFile(template);
      setTemplateMenuVisible(null);
    } catch (error) {
      console.error("Error exporting template:", error);
      alert("Failed to export template");
    }
  };

  // Merge system and user templates
  const allTemplates = useMemo(() => {
    const system = systemTemplates.map((t) => ({
      ...t,
      isSystem: true,
      templateId: t.id,
    }));
    const user = userTemplates.map((t) => ({
      id: t.templateId,
      name: t.name,
      author: t.author || null,
      description: t.description || null,
      image: t.image || null,
      isSystem: false,
      templateId: t.templateId,
    }));
    return [...system, ...user];
  }, [systemTemplates, userTemplates]);

  return (
    <div className="homepage-container">
      {/* {import.meta.env.VITE_ENV === "DEV" && (
        <img src={devBanner} className="banner" alt="FocusMap Logo" />
      )} */}
      {/* <h1 className="homepage-title" onClick={handleFocus365Click}>
        My Focus
      </h1> */}
      <BurgerMenu
        showMenu={showMenu}
        toggleMenu={toggleMenu}
        handleUserProfileClick={handleUserProfileClick}
        handleSettingsClick={handleSettingsClick}
        //handleFocusMapSiteClick={handleFocus365Click}
        handleSubscriptionPlansClick={handleSubscriptionPlansClick}
        handleTermsAndConditionsClick={handleTermsAndConditionsClick}
        handlePrivacyPolicyClick={handlePrivacyPolicyClick}
        handleSignOut={handleSignOut}
        isAuthenticated={true}
      />
      {showProfileModal && (
        <div className="homepage-modal">
          <div className="homepage-modal-content">
            <span className="homepage-close" onClick={closeProfileModal}>
              &times;
            </span>
            <h2>Profile</h2>
            {userProfile && (
              <div>
                {/* <p>Name: {userProfile.name ? "" : "-"}</p> */}
                <p>Email: {userProfile.email}</p>
                {/* <p>Created: {formatDate(userProfile.createdAt ? userProfile.createdAt : userProfile.timestamp)}</p> */}
                <p>
                  Plan: <strong>{userProfile.plan}</strong>
                  <a
                    href="https://www.focusmap.online/subscriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="subscription-link"
                  >
                    (more plans available <FaExternalLinkAlt /> )
                  </a>
                </p>
                <p>App Version: {import.meta.env.VITE_APP_VERSION}</p>
                <p>
                  Data Storage:{" "}
                  {useStore.getState().useLocalStorage ? (
                    <div title="Local Data">
                      <FaDatabase
                        size={14}
                        color="#3C5973"
                        className="homepage-storage-icon"
                      />{" "}
                      Local
                    </div>
                  ) : (
                    <div title="Cloud Data">
                      <FaCloud size={14} color="#3C5973" /> Cloud
                    </div>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {showSettingsModal && (
        <div className="homepage-modal">
          <div className="homepage-modal-content">
            <span
              className="homepage-close"
              onClick={() => setShowSettingsModal(false)}
            >
              &times;
            </span>
            <h2>Settings</h2>
            {userProfile && (
              <div>
                <p>Name: {userProfile.name}</p>
                <p>Email: {userProfile.email}</p>
              </div>
            )}
          </div>
        </div>
      )}
      <button className="help-button" onClick={handleHelp} title="Help Center">
        <IoHelpCircle size={32} />
      </button>

      <button
        className="data-button"
        onClick={handleDbSettingsClick}
        title="Data Location"
      >
        {useStore.getState().useLocalStorage ? (
          <FaDatabase size={30} />
        ) : (
          <FaCloud size={32} />
        )}
      </button>

      {/* <DataViewLayoutOptions
        layout={layout}
        onChange={(e) => setLayout(e.value as "grid" | "list")}
        className="data-view-layout-options"
      /> */}

      <TabView
        className="homepage-p-tabview-nav"
        activeIndex={activeTabIndex}
        onTabChange={(e) => setActiveTabIndex(e.index)}
      >
        <TabPanel header="My Maps" className="">
          <div className="card">
            <div className="homepage-search-row">
              <input
                type="text"
                placeholder="Search maps..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="homepage-search-input"
              />
            </div>
            <div className="flows-grid">
              {flows.length === 0 ? (
                <div className="homepage-empty-message">
                  Click on the bottom right button to create a new map
                </div>
              ) : (
                <>
                  {displayedFlows.map((flow) => (
                    <FlowCard
                      key={flow.id}
                      flow={flow}
                      handleFlowClick={handleFlowClick}
                      reSortData={reSortData}
                      loadFlows={loadFlows}
                    />
                  ))}
                  {/* Intersection observer target for lazy loading */}
                  {hasMore && (
                    <div
                      ref={observerTarget}
                      className="homepage-observer-target"
                    />
                  )}
                </>
              )}
            </div>
            {flows.length > 0 && displayedFlows.length === 0 && (
              <div className="homepage-no-matches">
                No maps match your search.
              </div>
            )}
          </div>
        </TabPanel>
        <TabPanel header="Templates">
          <div className="templates-tab-header">
            <div className="templates-tab-actions">
              <Button
                icon={<FaFileImport />}
                label="Import"
                onClick={handleImportTemplate}
                className="p-button-sm p-button-outlined"
              />
              <Button
                icon={<FaPlus />}
                label="Create Template"
                onClick={handleCreateTemplate}
                className="p-button-sm"
              />
            </div>
          </div>
          {allTemplates.length === 0 ? (
            <div className="homepage-empty-message">
              No templates found. Click "Create Template" to add one.
            </div>
          ) : (
            <div className="templates-grid">
              {allTemplates.map((t) => (
                <div
                  key={t.templateId}
                  className="template-card"
                  style={{ position: "relative" }}
                >
                  <div
                    className="template-card-content"
                    onClick={() => navigate(`/flow/new?template=${t.id}`)}
                  >
                    <div className="template-card-image-container">
                      <img
                        src={t.image || placeholder}
                        alt="Template"
                        className="template-card-image"
                      />
                    </div>
                    <div className="template-card-details">
                      <div className="template-headline">
                        {t.name}
                        {!t.isSystem && (
                          <span className="template-user-badge">User</span>
                        )}
                      </div>
                      <div className="template-description">
                        {t.isSystem ? (
                          <>
                            <div className="template-type-label">
                              System Template
                            </div>
                            {t.description && <div>{t.description}</div>}
                            {t.author && (
                              <div className="template-author-line">
                                Author: {t.author}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div>User template</div>
                            {t.description && <div>{t.description}</div>}
                            {t.author && (
                              <div className="template-author-line">
                                Author: {t.author}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {!t.isSystem && (
                        <div className="template-card-menu">
                          <FaEllipsisV
                            style={{
                              fontSize: "1rem",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTemplateMenuVisible(
                                templateMenuVisible === t.templateId
                                  ? null
                                  : t.templateId,
                              );
                            }}
                          />
                          {templateMenuVisible === t.templateId && (
                            <div className="template-menu-dropdown">
                              <div
                                className="template-menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const template = userTemplates.find(
                                    (ut) => ut.templateId === t.templateId,
                                  );
                                  if (template)
                                    handlePreviewTemplate(template.markdown);
                                }}
                              >
                                Preview
                              </div>
                              <div
                                className="template-menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const template = userTemplates.find(
                                    (ut) => ut.templateId === t.templateId,
                                  );
                                  if (template) handleEditTemplate(template);
                                }}
                              >
                                Modify
                              </div>
                              <div
                                className="template-menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const template = userTemplates.find(
                                    (ut) => ut.templateId === t.templateId,
                                  );
                                  if (template) handleExportTemplate(template);
                                }}
                              >
                                Export
                              </div>
                              <div
                                className="template-menu-item template-menu-item-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTemplate(t.templateId);
                                }}
                              >
                                Delete
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabPanel>
        <TabPanel header="Integrations & Modules">
          <div className="templates-grid">
            {integrations.map((integration) => {
              // Example: disable all except "onenote" (customize as needed)
              const isDisabled = integration.disabled;
              return (
                <div
                  key={integration.id}
                  className={`homepage-integration-item${isDisabled ? " is-disabled" : ""}`}
                >
                  <IntegrationCard
                    integration={integration}
                    handleIntegrationClick={() => {
                      switch (integration.id) {
                        case "onenote":
                          handleIntegrationClick(integration.id);
                          break;
                        case "lawyer":
                          handleLawyerIntegrationClick(integration.id);
                          break;
                        case "sustainability":
                          handleSustainabilityIntegrationClick(integration.id);
                          break;
                        case "microsoft365":
                          handleMicrosoft365IntegrationClick(integration.id);
                          break;
                        case "rapidlearningcycles":
                          handleRapidLearningCyclesClick();
                          break;
                        default:
                          alert("Work in Progress...");
                          break;
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
        </TabPanel>
      </TabView>
      <button
        id="create-map"
        title={activeTabIndex === 1 ? "Create New Template" : "Create New Map"}
        className="create-flow-button fixed-button"
        onClick={() => {
          if (activeTabIndex === 1) {
            // Templates tab - open template editor
            handleCreateTemplate();
          } else {
            // My Maps tab - create new flow
            handleCreateNewFlow(null);
          }
        }}
      >
        <FaPlus />
      </button>

      <HelpModal showHelpModal={showHelpModal} onClose={handleCloseHelpModal} />
      <WelcomeModal
        showHelpModal={showWelcomeModal}
        setShowHelpModal={setShowWelcomeModal}
        onClose={handleCloseWelcomeModal}
        setDoNotShowAgain={setDoNotShowAgain}
      />

      <TemplateEditorDialog
        visible={showTemplateEditor}
        template={editingTemplate}
        onClose={() => {
          setShowTemplateEditor(false);
          setEditingTemplate(null);
        }}
        onSave={handleSaveTemplate}
      />

      <Dialog
        visible={showTemplatePreview}
        onHide={() => setShowTemplatePreview(false)}
        header="Template Preview"
        style={{ width: "50vw" }}
        modal
      >
        <TemplatePreview markdown={previewMarkdown} />
      </Dialog>

      <LaywerTemplate
        visible={showLawyerTemplate}
        onHide={() => setShowLawyerTemplate(false)}
        onCreateCase={(data: LawyerData) => {
          setShowLawyerTemplate(false);
          handleCreateNewFlow("lawyer", data);
        }}
      />

      <OneNoteIntegrationModal
        show={showOneNoteModal}
        onClose={() => setShowOneNoteModal(false)}
      />
      {showMapLimitAlert && (
        <CustomAlert
          title="Limit Reached"
          message=""
          htmlMessage={
            <>
              You have reached the maximum number of maps for the free plan.
              Please visit our{" "}
              <a
                href="https://www.focusmap.online/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
              >
                subscription page
              </a>{" "}
              for more options.
            </>
          }
          onClose={() => setShowMapLimitAlert(false)}
        />
      )}

      {showFeatureAlert && (
        <CustomAlert
          title="💎Premium Plan Feature"
          message=""
          htmlMessage={
            <>
              Upgrade to a Paid Plan and get immediate access to this feature.
              Please visit our{" "}
              <a
                href="https://www.focusmap.online/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
              >
                subscription page
              </a>{" "}
              for more options.
            </>
          }
          onClose={() => setShowFeatureAlert(false)}
        />
      )}

      {alertMessage && (
        <CustomAlert
          title={alertTitle}
          message={alertMessage}
          htmlMessage={null}
          onClose={handleCloseAlert}
        />
      )}

      <div className="card flex justify-content-center">
        <Dialog
          visible={userSettingsVisible}
          modal
          header={headerElement}
          footer={footerContent}
          className="homepage-data-dialog"
          onHide={() => {
            setUserSettingsVisible(false);
          }}
        >
          <div className="card flex justify-content-center">
            <SelectButton
              className="homepage-data-selectbutton flex align-self-center align-content-center align-items-center"
              value={dataOnline}
              onChange={(e) => setDataOnline(e.value)}
              options={options}
            />
            <p className="homepage-data-note">
              *data in the cloud offers automatic backup, map sharing and more
            </p>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default Homepage;
